if (!customElements.get('add-to-cart')) {
  class AddToCart extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      if (!this.button) return;
      
      this.enableAddToCart = this.button.dataset.hasOnlyDefaultVariant === 'true';
      this.productListInfo = this.closest('.upsell-card')?.querySelector('.upsell-card__text-and-variant-select-wrap');
      this.spinner = this.button.querySelector('.add-to-cart-button-spinner');
      this.buttonText = this.button.querySelector('.add-to-cart-button-text');
      this.variantId = this.button.dataset.variantId;
      this.addToCartText = this.button.dataset.addToCartText;

      if (this.variantId) {
        this.button.addEventListener('click', this.handleButtonClick.bind(this));
      }
    }

    handleButtonClick(event) {
      event.preventDefault();
      if (this.enableAddToCart) {
        this.addToCart();
      } else {
        this.displayVariantSelector();
      }
    }

    async addToCart() {
      try {
        const response = await this.addVariantToCart();
      } catch (error) {
        console.error('Error adding item to cart:', error);
      } finally {
        const event = new CustomEvent('upsell-product-added', {});
        document.dispatchEvent(event);
      }
    }

    async addVariantToCart() {
      const variantId = this.variantId; // Assuming this.variantId is where the ID is stored
    
      if (!variantId || isNaN(parseInt(variantId))) {
        console.error('Error: Invalid or missing variantId.', variantId);
        return;
      }
    
      const formData = {
        'items': [{
          'id': parseInt(variantId),
          'quantity': 1
        }]
      };
    
      let shopifyRoot = '/'; // Default to root if Shopify object isn't fully available
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        shopifyRoot = window.Shopify.routes.root;
      }
      // Ensure no double slashes if shopifyRoot already ends with '/'
      const cartAddUrl = `${shopifyRoot.replace(/\/$/, '')}/cart/add.js`;
    
      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formData)
        });
    
        if (!response.ok) {
          // If response is not OK, try to get more details from the body
          const errorBody = await response.json().catch(() => response.text()); // Try to parse as JSON, fallback to text
          console.error('Error response body:', errorBody);
          // Shopify often returns a 'description' or 'message' field in the JSON error
          let errorMessage = `HTTP error ${response.status}: ${response.statusText}`;
          if (typeof errorBody === 'object' && errorBody !== null && errorBody.description) {
            errorMessage += ` - ${errorBody.description}`;
          } else if (typeof errorBody === 'object' && errorBody !== null && errorBody.message) {
            errorMessage += ` - ${errorBody.message}`;
          } else if (typeof errorBody === 'string') {
            errorMessage += ` - ${errorBody}`;
          }
          throw new Error(errorMessage);
        }
    
        const responseData = await response.json();
        return responseData;
    
      } catch (error) {
        console.error('Error during addVariantToCart:', error);
      }
    }

    displayVariantSelector() {
      const template = this.querySelector('template');
      
      if (!template) return;

      const variantSelectors = template.content?.querySelector('.select-product-variants-selector')?.cloneNode(true);
      if (variantSelectors && this.productListInfo) {
        this.productListInfo.appendChild(variantSelectors);
        this.allVariants = this.productListInfo.querySelector('.product-form__variants')
        variantSelectors.querySelectorAll('.watch-for-option-change')?.forEach(selector => {
          selector.addEventListener('change', this.handleVariantChange.bind(this));
        })

        this.selectFirstAvailableVariant(variantSelectors);
      }
    }

    selectFirstAvailableVariant() {
      const select1 = this.productListInfo.querySelector('.watch-for-option-change:nth-of-type(1)');
      const select2 = this.productListInfo.querySelector('.watch-for-option-change:nth-of-type(2)'); // Might be null
  
      if (!select1) { return;}
  
      // 1. Parse all purchasable variants and their data from the master list
      const allVariantOptionsData = Array.from(this.allVariants.querySelectorAll('option')).map(opt => ({
          id: opt.value,
          option1Text: opt.getAttribute('data-option1')?.trim() || null, // Ensure null if empty/missing
          option2Text: opt.getAttribute('data-option2')?.trim() || null, // Ensure null if empty/missing
          isAvailable: opt.getAttribute('data-available') === 'true'
      }));
  
      // 2. Find the first variant marked as available
      const firstAvailableVariant = allVariantOptionsData.find(variant => variant.isAvailable);
  
      if (!firstAvailableVariant) {
          if (typeof this.handleVariantChange === 'function') {
              this.handleVariantChange(null);
          } else {
              this.variantId = null;
              this.updateAddToCartButton();
          }
          return;
      }
  
      // 3. Try to set the select dropdowns to match the firstAvailableVariant
      let canSetSelect1 = !firstAvailableVariant.option1Text; // True if variant doesn't specify an option1
      let targetSelect1Value = null;
  
      if (firstAvailableVariant.option1Text) {
          const optionElementInSelect1 = Array.from(select1.options).find(
              opt => opt.innerText.trim() === firstAvailableVariant.option1Text
          );
          if (optionElementInSelect1) {
              targetSelect1Value = optionElementInSelect1.value;
              canSetSelect1 = true;
          } else {
              console.warn(`selectFirstAvailableVariant: Could not find option "${firstAvailableVariant.option1Text}" in select1 for variant ${firstAvailableVariant.id}.`);
              canSetSelect1 = false;
          }
      }
  
      let canSetSelect2 = true; // Assume true if select2 doesn't exist or variant doesn't specify option2
      let targetSelect2Value = null;
  
      if (select2) { // Only proceed if select2 actually exists
          if (firstAvailableVariant.option2Text) { // If the variant expects an option2
              const optionElementInSelect2 = Array.from(select2.options).find(
                  opt => opt.innerText.trim() === firstAvailableVariant.option2Text
              );
              if (optionElementInSelect2) {
                  targetSelect2Value = optionElementInSelect2.value;
                  canSetSelect2 = true;
              } else {
                  console.warn(`selectFirstAvailableVariant: Could not find option "${firstAvailableVariant.option2Text}" in select2 for variant ${firstAvailableVariant.id}.`);
                  canSetSelect2 = false;
              }
          } else {
              // Variant doesn't specify an option2Text, so select2 setting is not dictated by this variant
              canSetSelect2 = true; // No specific option2 needed for this variant
          }
      }
      // If select2 does not exist, canSetSelect2 remains true (no action needed for select2).
  
      // 4. If we found corresponding options in dropdowns (or they weren't needed), update select values
      if (canSetSelect1 && canSetSelect2) {
          let changedSomething = false;
          if (firstAvailableVariant.option1Text && targetSelect1Value !== null) {
              if (select1.value !== targetSelect1Value) {
                  select1.value = targetSelect1Value;
                  changedSomething = true;
              }
          }
          // else: if option1Text is not specified by variant, select1 is not changed by this logic.
  
          if (select2 && firstAvailableVariant.option2Text && targetSelect2Value !== null) {
              if (select2.value !== targetSelect2Value) {
                  select2.value = targetSelect2Value;
                  changedSomething = true;
              }
          }
          // else: if option2Text is not specified by variant (or select2 doesn't exist), select2 is not changed.
          this.handleVariantChange(null); // Pass null or a mock event if your handler needs one
      } else {
          console.warn(`selectFirstAvailableVariant: Could not set dropdowns to fully match the first available variant (${firstAvailableVariant.id}). Current selections will be used.`);
          // Even if we couldn't set perfectly, run handleVariantChange with current (possibly default) select values.
          if (typeof this.handleVariantChange === 'function') {
              this.handleVariantChange(null);
          } else {
              this.variantId = null; // Ensure variantId reflects the inability to select
              this.updateAddToCartButton();
          }
      }

      this.enableAddToCart = true;
      this.updateAddToCartButton();
  }

    handleVariantChange(event) {
      const select1 = this.productListInfo.querySelector('.watch-for-option-change:nth-of-type(1)');
      const select2 = this.productListInfo.querySelector('.watch-for-option-change:nth-of-type(2)');
  
      if (!select1) {
          console.warn('Select1 not found.');
          this.updateAddToCartButton();
          return;
      }
  
      const allVariantOptionsData = Array.from(this.allVariants.querySelectorAll('option')).map(opt => ({
          value: opt.value,
          option1: opt.getAttribute('data-option1'),
          option2: opt.getAttribute('data-option2'),
          available: opt.getAttribute('data-available') === 'true'
      }));
  
      const selectedOption1Value = select1.value;
      const selectedOption1Text = select1.options[select1.selectedIndex]?.innerText.trim();
  
      let selectedOption2Text = null;
      if (select2) {
          selectedOption2Text = select2.options[select2.selectedIndex]?.innerText.trim();
      }
  
      // Helper function to enable/disable options
      const updateOptionDisabledState = (optionElement, available) => {
          if (available) {
              optionElement.removeAttribute('disabled');
          } else {
              optionElement.setAttribute('disabled', 'disabled');
          }
      };
  
      // Update options for select1 (e.g., colors)
      Array.from(select1.options).forEach(colorOption => {
          const colorOptionText = colorOption.innerText.trim();
          const variantMatching = allVariantOptionsData.find(variantData =>
              variantData.option1 === colorOptionText &&
              (!select2 || variantData.option2 === selectedOption2Text) // Match option2 only if select2 exists
          );
          updateOptionDisabledState(colorOption, variantMatching ? variantMatching.available : false);
      });
  
      // Update options for select2 (e.g., sizes), only if select2 exists
      if (select2 && selectedOption1Text) {
          Array.from(select2.options).forEach(sizeOption => {
              const sizeOptionText = sizeOption.innerText.trim();
              const variantMatching = allVariantOptionsData.find(variantData =>
                  variantData.option1 === selectedOption1Text &&
                  variantData.option2 === sizeOptionText
              );
              updateOptionDisabledState(sizeOption, variantMatching ? variantMatching.available : false);
          });
      }
  
      // Find the currently selected variant
      const currentSelectedVariant = allVariantOptionsData.find(variantData =>
          variantData.option1 === selectedOption1Text &&
          (!select2 || variantData.option2 === selectedOption2Text)
      );
  
      if (currentSelectedVariant) {
          this.variantId = currentSelectedVariant.value;
      } else {
          this.variantId = null; // Or some default/error state
      }
  
      this.updateAddToCartButton();
  }

    updateAddToCartButton() {
      if (this.variantId && this.enableAddToCart) {
        this.button.dataset.variantId = this.variantId;
        if (this.buttonText) {
          this.buttonText.textContent = this.addToCartText;
        }
      }
    }
  }

  customElements.define('add-to-cart', AddToCart);

  // Use MutationObserver to watch for new add-to-cart elements
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.querySelector('add-to-cart')) {
          new AddToCart(node.querySelector('add-to-cart'));
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
